import {
  ApproveAllowancePreviewMode,
  SmartAccountType,
  WalletType,
} from "@wallet-manager/node-types/dist/src/postgres/const";
import BigNumber from "bignumber.js";
import { groupBy, isEmpty, isNil, keyBy } from "lodash";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  postBatchApproveAllowancePreview,
  postBatchApproveAllowanceSubmit,
} from "src/api/merchant";
import { useChains } from "src/components/FetchConfig";
import { VersionTabsArr } from "src/components/VersionTabs";
import { cleanParams } from "src/features/common/helpers/cleanParams";
import { useAlerting, useTranslation } from "src/hooks";
import { enumChainIdToType } from "src/utils/constant";
import {
  amountDivideDecimals,
  amountToCryptoAmount,
  displayAmount,
  findChainInfo,
  findDecimalByChainNameAndAsset,
  isOperationSmartAccount,
} from "src/utils/helper";
import {
  BatchApproveAllowanceFilterFields,
  BatchApproveAllowanceFormContext,
  BatchApproveAllowanceFormContextType,
  BatchApproveAllowancePreviewParams,
  initBatchApproveAllowanceFilterFields,
  useBatchApproveAllowanceFormContext,
} from "./FormContext";
import {
  BatchApproveAllowancePreviewAddressData,
  BatchApproveAllowancePreviewContext,
  BatchApproveAllowancePreviewContextType,
  BatchApproveAllowancePreviewData,
  BatchApproveAllowanceSubmitParams,
  SendingAddressesMap,
  useBatchApproveAllowancePreviewContext,
} from "./PreviewContext";

export {
  useBatchApproveAllowanceFormContext,
  useBatchApproveAllowancePreviewContext,
};

export const useBatchApproveAllowanceContext = () => {
  const formContext = useBatchApproveAllowanceFormContext();
  const previewContext = useBatchApproveAllowancePreviewContext();

  return useMemo(
    () => ({ ...formContext, ...previewContext }),
    [formContext, previewContext],
  );
};

const BatchApproveAllowanceContextProvider = ({
  children,
}: PropsWithChildren) => {
  const { alerting } = useAlerting();
  const { selectChainByPieces, chainObj } = useChains();
  const { t, tc } = useTranslation("batchApproveAllowance");

  //version tabs for wallet major version
  const [walletMajorVersion, setWalletMajorVersion] = useState(0);
  const [tabsArr, setTabsArr] = useState<VersionTabsArr[]>([]);

  const [fields, setFields] = useState<BatchApproveAllowanceFilterFields>(
    initBatchApproveAllowanceFilterFields,
  );
  const [previewData, setPreviewData] =
    useState<BatchApproveAllowancePreviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [sendingAddressesMap, setSendingAddressesMap] =
    useState<SendingAddressesMap>(new Map());

  const [invokerAddressDictionary, setInvokerAddressDictionary] = useState<
    Record<number, { invokerAddress: string }>
  >({});

  //handle search Params
  const [searchedParams] = useSearchParams();
  const [isInit, setisInit] = useState(true);

  const chainCodeParams = searchedParams.get("chainCode");
  const currencyParams = searchedParams.get("currency");
  const spenderAddressParams = searchedParams.get("spenderAddress");
  const isSmartAccountParams = searchedParams.get("isSmartAccount");
  const isNoSearchParams =
    isNil(chainCodeParams) ||
    isNil(currencyParams) ||
    isNil(spenderAddressParams) ||
    isNil(isSmartAccountParams);

  useEffect(() => {
    if (isNoSearchParams) return;

    const isSmartAccountBoolean =
      isSmartAccountParams === "true" ? true : false;

    setFields((f) => ({
      ...f,
      chainName: chainCodeParams,
      currency: currencyParams,
      spenderAddress: spenderAddressParams,
      isPreparedAddresses: true,
      isSmartAccount: isSmartAccountBoolean,
    }));
  }, [searchedParams]);

  const previewParams: BatchApproveAllowancePreviewParams = useMemo(() => {
    const {
      chainName,
      currency: assetName,
      spenderAddress,
      isSmartAccount,
      threshold,
      maxNumberOfAddresses: limit,
      isPreparedAddresses,
    } = fields;

    const {
      chain_id: chainId = "",
      chain_type: chainType = "",
      support_smart_account: supportSmartAccount,
    } = selectChainByPieces({
      chain_name: chainName,
    });

    const decimals = Number(
      findDecimalByChainNameAndAsset(
        String(chainType),
        String(chainId),
        assetName,
      ),
    );

    const getSmartAccountType = () => {
      if (!supportSmartAccount) {
        return undefined;
      }
      if (!isSmartAccount) {
        return SmartAccountType.EOA;
      }
      switch (String(chainType)) {
        case enumChainIdToType.TRON:
          return SmartAccountType.TronRentalEnergyBandwidth;
        case enumChainIdToType.ETH:
          return SmartAccountType.Kernel;
        default:
          return undefined;
      }
    };
    return {
      chainId,
      chainType,
      assetName,
      spenderAddress,
      threshold:
        !threshold || isNaN(decimals) || isPreparedAddresses
          ? undefined
          : amountToCryptoAmount(threshold, decimals),
      limit,
      smartAccountType: getSmartAccountType(),
      previewMode: isPreparedAddresses
        ? ApproveAllowancePreviewMode.WalletsInWalletAllowance
        : ApproveAllowancePreviewMode.AllWallets,
    };
  }, [fields, selectChainByPieces, findDecimalByChainNameAndAsset]);

  const isInvalidParams =
    !previewParams.chainType ||
    !previewParams.chainId ||
    (previewParams.previewMode === ApproveAllowancePreviewMode.AllWallets &&
      !previewParams.threshold);

  //handle click preview button when init and got data in search params
  useEffect(() => {
    if (isNoSearchParams) {
      setisInit(false);
      return;
    }
    if (!isInit || isInvalidParams) return;
    onPreview();
  }, [previewParams, isInit]);

  const walletMajorVersionSearchParams =
    searchedParams.get("walletMajorVersion");

  const onPreview = useCallback(async () => {
    if (isInvalidParams) {
      return alerting("error", t("invalid_params"));
    }
    if (previewParams.limit && Number(previewParams.limit) > 1000) {
      return alerting("warning", t("maxLargerThanOneThousand"));
    }
    setInvokerAddressDictionary({});
    setIsLoading(true);
    const res: BatchApproveAllowancePreviewAddressData[] =
      await postBatchApproveAllowancePreview(cleanParams(previewParams));
    setIsLoading(false);
    if (!res || res.length === 0) {
      setPreviewData(null);
      return alerting("warning", t("no_data_to_preview"));
    }
    setPreviewData({ previewParams, addressData: res });

    //tabs array handling
    const groupedResult = groupBy(res, "wallet_major_version");
    const tabs: VersionTabsArr[] = Object.entries(groupedResult).map(
      (item) => ({
        walletMajorVersion: Number(item[0]),
        walletCounts: item[1]?.length,
      }),
    );
    const sortedTabs = tabs.sort(
      (itemA, itemB) => itemB.walletMajorVersion - itemA.walletMajorVersion,
    );
    setTabsArr(sortedTabs);
    const localWalletMajorVersion =
      isNil(walletMajorVersionSearchParams) || !isInit //each time when preview clicked, wallet major version will never follow the search param
        ? sortedTabs[0].walletMajorVersion
        : Number(walletMajorVersionSearchParams ?? 0);

    //set sending Address
    setSendingAddressesMap(
      new Map(
        res
          .filter(
            (item) => item.wallet_major_version === localWalletMajorVersion,
          )
          .map(
            ({
              wallet_address,
              wallet_balance,
              decimals,
              wallet_type,
              wallet_major_version,
            }) => [
              wallet_address,
              {
                walletBalance: amountDivideDecimals(wallet_balance, decimals),
                walletType: wallet_type,
                isSelected: true,
                walletMajorVersion: wallet_major_version,
              },
            ],
          ),
      ),
    );

    setWalletMajorVersion(localWalletMajorVersion);
    setisInit(false);
  }, [previewParams]);

  const submitParams: BatchApproveAllowanceSubmitParams = useMemo(() => {
    const {
      chainType = "",
      chainId = "",
      assetName = "",
      spenderAddress = "",
      smartAccountType,
    } = previewData?.previewParams || {};

    const orders = Array.from(sendingAddressesMap.entries()).reduce(
      (
        acc: { walletAddress: string; walletType: WalletType }[],
        [walletAddress, { walletType, isSelected }],
      ) => {
        if (!isSelected) {
          return acc;
        }
        return [...acc, { walletAddress, walletType }];
      },
      [],
    );

    return {
      assetName,
      spenderAddress,
      chainType: String(chainType),
      chainId,
      orders,
      invokerAddress:
        invokerAddressDictionary[walletMajorVersion]?.invokerAddress,
      smartAccountType,
    };
  }, [
    previewData,
    sendingAddressesMap,
    invokerAddressDictionary,
    walletMajorVersion,
  ]);

  const toDisplayAmountWithCurrency = useCallback(
    (amount: string | number) =>
      `${displayAmount(amount, 0)} ` + submitParams.assetName,
    [displayAmount, submitParams.assetName],
  );

  const addressesSummary = useMemo(() => {
    let selectedCount = 0;

    const versonedSendingAddressMap = Array.from(
      sendingAddressesMap.values(),
    ).filter((item) => item.walletMajorVersion === walletMajorVersion);

    const totalBalance = versonedSendingAddressMap.reduce(
      (acc, { walletBalance, isSelected }) => {
        if (!isSelected) {
          return acc;
        }
        selectedCount++;
        return acc.plus(walletBalance);
      },
      BigNumber(0),
    );

    return {
      totalCount: versonedSendingAddressMap.length,
      selectedCount,
      totalBalance: totalBalance.toFixed(),
    };
  }, [sendingAddressesMap, submitParams.assetName, walletMajorVersion]);

  const isSecondLayerToken = useMemo(
    () =>
      Object.values(chainObj).some(
        (chain) =>
          chain.chain_id === submitParams.chainId &&
          chain.chain_type === Number(submitParams.chainType) &&
          chain.native_asset_name !== submitParams.assetName,
      ),
    [chainObj, submitParams],
  );

  const invokerAddressSelectionDependency = {
    isSecondLayerToken,
    isSmartAccount:
      !!submitParams.smartAccountType &&
      isOperationSmartAccount(submitParams.smartAccountType),
  };

  const getIsInvokerAddressSelectionShow = () => {
    if (invokerAddressSelectionDependency.isSmartAccount) {
      return false;
    }
    if (!isSecondLayerToken) {
      return false;
    }
    return true;
  };

  const onSubmit = useCallback(async () => {
    if (getIsInvokerAddressSelectionShow()) {
      if (isEmpty(submitParams.invokerAddress)) {
        return alerting(
          "error",
          tc("phSelection", { fieldName: t("invoker_address") }),
        );
      }
    }
    setIsLoading(true);
    const res = await postBatchApproveAllowanceSubmit(submitParams);
    if (!res) {
      setIsLoading(false);
      return;
    }
    alerting("success", t("submit_success"));
    setTimeout(() => {
      setIsLoading(false);
      window.location.href = window.location.pathname;
    }, 1000);
  }, [submitParams]);

  const onReset = useCallback(() => {
    setFields(initBatchApproveAllowanceFilterFields);
    setPreviewData(null);
    setInvokerAddressDictionary({});
  }, []);

  const formContextValue: BatchApproveAllowanceFormContextType = useMemo(
    () => ({
      isLoading,
      fields,
      setFields,
      onPreview,
      previewParams,
      translate: t,
    }),
    [isLoading, fields, onPreview, previewParams, t],
  );

  const previewContextValue: BatchApproveAllowancePreviewContextType = useMemo(
    () => ({
      translate: t,
      previewData,
      submitParams,
      onSubmit,
      onReset,
      sendingAddressesMap,
      setSendingAddressesMap,
      invokerAddressDictionary,
      setInvokerAddressDictionary,
      invokerAddressSelectionDependency,
      getIsInvokerAddressSelectionShow,
      toDisplayAmountWithCurrency,
      summaryDetails: {
        addressesSummary,
        currency: submitParams.assetName,
        chainName:
          findChainInfo(submitParams.chainType, submitParams.chainId)?.name ||
          "",
        spenderAddress: submitParams.spenderAddress,
      },
      walletMajorVersion,
      setWalletMajorVersion,
      tabsArr,
    }),
    [
      t,
      previewData,
      onSubmit,
      onReset,
      invokerAddressDictionary,
      setInvokerAddressDictionary,
      isSecondLayerToken,
      toDisplayAmountWithCurrency,
      isOperationSmartAccount,
      sendingAddressesMap,
      addressesSummary,
      submitParams,
      walletMajorVersion,
      setWalletMajorVersion,
    ],
  );

  return (
    <BatchApproveAllowanceFormContext.Provider value={formContextValue}>
      <BatchApproveAllowancePreviewContext.Provider value={previewContextValue}>
        {children}
      </BatchApproveAllowancePreviewContext.Provider>
    </BatchApproveAllowanceFormContext.Provider>
  );
};

export default BatchApproveAllowanceContextProvider;
